import { useContext, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import PrimaryButton from '../../../../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../../../../global/components/buttons/secondary-button/SecondaryButton'
import styles from '../styles/styles.module.css'
import useFetch from '../../../../../../../global/hooks/useFetchHook'
import {
  fetchData,
  IFetchDataProps,
} from '../../../../../../../global/utils/fetch'
import { ToastNotificationContext } from '../../../../../../../global/context/toast-context/ToastNotificationContext'
import {
  getAvailableSecondaryTechnicians,
  removeSecondaryTechnicianFromRoute,
  setSecondaryTechnicianOnRoute,
} from '../../../../../../../schedule/components/api'
import { ITechnician } from '../../../../../../../../../app/entities/Technician'
import { IRoute } from '../../../../../../../../../app/entities/Route'
import { AvailableTechniciansDropDown } from '../tech-assignment/TechAssignment'

interface ISecondaryTechnicianProps {
  route: IRoute
  handleClose: Function
  refetch: () => void
}

/**
 * FRON-1678: add or remove a training ride-along (secondary technician) on an
 * optimized route. The ride-along sees the route read-only in the tech app
 * and splits its tips 50/50 with the primary.
 */
export default function SecondaryTechnician({
  route,
  handleClose,
  refetch,
}: ISecondaryTechnicianProps) {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [selectedTechnicianObjectId, setSelectedTechnicianObjectId] =
    useState('')
  const [isSaving, setIsSaving] = useState(false)

  const { location, technician, secondaryTechnician, objectId } = route

  const { data: availableTechnicians, isLoading } = useFetch<ITechnician[]>(
    getAvailableSecondaryTechnicians(objectId),
  )

  async function submit(request: IFetchDataProps, successMessage: string) {
    setIsSaving(true)
    try {
      await fetchData(request)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: successMessage,
        severity: 'success',
      })
      refetch()
      handleClose()
    } catch (error) {
      console.error(error)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Could not update the ride-along technician.',
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className={styles.progressContainer}>
        <CircularProgress />
      </div>
    )
  }

  return (
    <div className={styles.inputsContainer}>
      <div className={styles.topInputsContainer}>
        Location: {location?.name}
      </div>
      <div className={styles.topInputsContainer}>
        Primary Technician: {technician?.name}
      </div>
      <div className={styles.topInputsContainer}>
        A ride-along technician sees this route read-only in the tech app and
        splits its tips 50/50 with the primary. Mark them off in Day Settings so
        route optimization does not give them a route of their own.
      </div>
      {secondaryTechnician && (
        <div className={styles.topInputsContainer}>
          Current ride-along: <strong>{secondaryTechnician.name}</strong>
        </div>
      )}
      <div className={styles.headerTwo}>Available Technicians</div>
      <div className={styles.topInputsContainer}>
        {availableTechnicians?.length ? (
          <AvailableTechniciansDropDown
            onTechChange={(val: string) => setSelectedTechnicianObjectId(val)}
            availableTechnicians={availableTechnicians}
          />
        ) : (
          <div className={styles.noTechs}>
            No Technicians available to ride along
          </div>
        )}
      </div>
      <div className={styles.buttonContainer}>
        <SecondaryButton
          buttonName='Cancel'
          onClick={() => handleClose()}
        />
        {secondaryTechnician && (
          <SecondaryButton
            buttonName='Remove'
            disabled={isSaving}
            onClick={() =>
              submit(
                removeSecondaryTechnicianFromRoute(objectId),
                'Ride-along technician removed.',
              )
            }
          />
        )}
        <PrimaryButton
          buttonName='Save'
          disabled={isSaving || selectedTechnicianObjectId === ''}
          onClick={() =>
            submit(
              setSecondaryTechnicianOnRoute(
                objectId,
                selectedTechnicianObjectId,
              ),
              'Ride-along technician added.',
            )
          }
        />
      </div>
    </div>
  )
}
